import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Upload, CalendarDays, ListChecks, ShieldCheck, Menu, X, ArrowRight,
  FileText, CheckCircle2, GraduationCap, BookOpen, Sparkles, Clock,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { captureRef } from "@/lib/referral";
import { AuthModal } from "@/components/AuthModal";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const Nav = ({ user, navigate, onAuth }) => {
  const [open, setOpen] = useState(false);
  const links = [
    { label: "How It Works", id: "how" },
    { label: "Pricing", id: "pricing" },
    { label: "FAQ", id: "faq" },
  ];
  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/90 border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
        <button onClick={() => navigate("/")} data-testid="nav-logo-btn"><Logo /></button>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button key={l.id} onClick={() => go(l.id)} data-testid={`nav-${l.id}`}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">{l.label}</button>
          ))}
          {user ? (
            <Button onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-btn"
              className="bg-slate-900 hover:bg-slate-800 rounded-lg">Go to Dashboard</Button>
          ) : (
            <>
              <button onClick={() => onAuth("login")} data-testid="nav-login-btn"
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Log In</button>
              <Button onClick={() => onAuth("register")} data-testid="nav-getstarted-btn"
                className="bg-slate-900 hover:bg-slate-800 rounded-lg">Get Started</Button>
            </>
          )}
        </nav>
        <button className="md:hidden text-slate-700" onClick={() => setOpen(!open)} data-testid="nav-hamburger">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-3">
          {links.map((l) => (
            <button key={l.id} onClick={() => go(l.id)} className="block w-full text-left text-sm font-medium text-slate-700 py-1">{l.label}</button>
          ))}
          {user ? (
            <Button onClick={() => navigate("/dashboard")} className="w-full bg-slate-900 rounded-lg">Go to Dashboard</Button>
          ) : (
            <>
              <button onClick={() => onAuth("login")} className="block w-full text-left text-sm font-medium text-slate-700 py-1">Log In</button>
              <Button onClick={() => onAuth("register")} className="w-full bg-slate-900 rounded-lg">Get Started</Button>
            </>
          )}
        </div>
      )}
    </header>
  );
};

const DashboardMockup = () => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
    <div className="flex items-center gap-1.5 px-4 py-3 border-b border-slate-100 bg-slate-50">
      <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
      <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
      <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
      <span className="ml-3 text-xs font-medium text-slate-400">Syllabify — Fall 2026</span>
    </div>
    <div className="p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-slate-900">Upcoming</h4>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">3 due soon</span>
      </div>
      <div className="space-y-3">
        {[
          { t: "Biology Chapter 3", d: "Aug 25", c: "bg-emerald-500", days: "5d", dc: "text-slate-500" },
          { t: "English Essay 1", d: "Aug 28", c: "bg-blue-500", days: "8d", dc: "text-slate-500" },
          { t: "Math Exam 1", d: "Sep 2", c: "bg-amber-500", days: "3d", dc: "text-red-600" },
        ].map((e) => (
          <div key={e.t} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3">
            <span className={`h-2.5 w-2.5 rounded-full ${e.c}`} />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-900">{e.t}</p>
              <p className="text-xs text-slate-500">{e.d}</p>
            </div>
            <span className={`text-xs font-semibold ${e.dc}`}>{e.days}</span>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-lg bg-slate-50 border border-slate-100 p-3">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
          <span>Progress</span><span>7 of 10 tasks</span>
        </div>
        <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full w-[70%] rounded-full bg-blue-600" />
        </div>
      </div>
    </div>
  </div>
);

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const openAuth = (tab = "login") => { setAuthTab(tab); setAuthOpen(true); };

  useEffect(() => {
    captureRef();
  }, []);

  const onGetStarted = () => (user ? navigate("/upload") : openAuth("register"));

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultTab={authTab} key={authTab} />
      <Nav user={user} navigate={navigate} onAuth={openAuth} />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 mb-6">
              <Sparkles className="h-3.5 w-3.5" /> Pay once. Use it every semester.
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.1]">
              Turn your syllabus into a plan.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Upload your syllabus and Syllabify automatically organizes your assignments, exams, projects,
              and important dates into one simple semester planner.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Button onClick={onGetStarted} data-testid="hero-upload-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-6 rounded-lg text-base font-medium shadow-sm">
                <Upload className="h-5 w-5 mr-1" /> Upload My Syllabus
              </Button>
              <span className="text-sm text-slate-500">One-time payment. No subscription.</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <DashboardMockup />
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">How it works</h2>
          <p className="mt-3 text-slate-600">Three simple steps from messy PDF to organized semester.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Upload", d: "Upload your syllabus PDF or DOCX.", icon: Upload },
            { n: "02", t: "Syllabify", d: "We identify your assignments, exams, projects, and important dates.", icon: FileText },
            { n: "03", t: "Plan", d: "Get a clean semester calendar and task list you can actually use.", icon: CalendarDays },
          ].map((s) => (
            <div key={s.n} className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <s.icon className="h-5 w-5" />
                </span>
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{s.n}</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900">{s.t}</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Everything you need to stay on track</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: CalendarDays, t: "Semester calendar", d: "Month, week, and list views with course colors." },
              { icon: ListChecks, t: "Assignment tracking", d: "Mark tasks not started, in progress, or completed." },
              { icon: BookOpen, t: "Multiple courses", d: "Upload every syllabus and keep each class organized." },
              { icon: GraduationCap, t: "Grade calculator", d: "See what you need on the final to hit your target." },
              { icon: Clock, t: "Study plans", d: "Turn big exams into a simple day-by-day schedule." },
              { icon: ShieldCheck, t: "You stay in control", d: "Review and edit every extracted date before it's saved." },
            ].map((f) => (
              <div key={f.t} className="rounded-xl border border-slate-200 p-6 hover:border-slate-300 transition-colors">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-700 mb-4">
                  <f.icon className="h-5 w-5" />
                </span>
                <h3 className="font-semibold text-slate-900">{f.t}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXAMPLE TRANSFORMATION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">From syllabus text to a clean event</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6 items-stretch">
          <div className="rounded-xl border border-slate-200 bg-white p-8">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Before</span>
            <p className="mt-4 text-slate-600 leading-relaxed font-mono text-sm bg-slate-50 rounded-lg p-4 border border-slate-100">
              "September 10 — Exam 1 covering chapters 1–4. Bring a scantron and calculator. Worth 15% of your final grade."
            </p>
          </div>
          <div className="rounded-xl border border-blue-200 bg-white p-8">
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-500">After</span>
            <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50/60 p-5">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 text-xs font-medium">
                BIO 101
              </span>
              <div className="mt-3 flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-slate-500" />
                <span className="font-semibold text-slate-900">Exam 1</span>
              </div>
              <p className="mt-1 text-sm text-slate-500">September 10 · Chapters 1–4 · Weight 15%</p>
              <span className="mt-3 inline-block text-xs font-medium text-amber-600">Status: Upcoming</span>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">Pay once. Stay organized all semester.</h2>
            <p className="mt-3 text-slate-300">No monthly subscription. No recurring charges.</p>
          </div>
          <div className="max-w-md mx-auto rounded-2xl bg-white p-8 shadow-2xl">
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-bold text-slate-900">$4.99</span>
              <span className="text-slate-500">one-time</span>
            </div>
            <p className="mt-2 text-sm font-medium text-slate-600">Syllabify Lifetime Access</p>
            <ul className="mt-6 space-y-3">
              {["Unlimited syllabus uploads", "Multiple courses", "Full calendar & assignment tracking",
                "Grade calculator & study plans", "Edit extracted events", "Permanent access — every semester"].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" /> {b}
                </li>
              ))}
            </ul>
            <Button onClick={onGetStarted} data-testid="pricing-cta-btn"
              className="mt-8 w-full bg-blue-600 hover:bg-blue-700 py-6 rounded-lg text-base font-medium">
              Unlock Syllabify — $4.99 <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 text-center mb-10">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="w-full">
          {[
            ["Is this a subscription?", "No. Syllabify is a one-time $4.99 purchase. Pay once and use it for every semester."],
            ["What files can I upload?", "PDF and DOCX syllabus files up to 15MB."],
            ["Does Syllabify do my homework?", "No. Syllabify helps organize your academic workload — it doesn't complete assignments for you."],
            ["Can I use it for multiple classes?", "Yes, with the paid version you can upload unlimited syllabi and courses."],
            ["Can I edit the dates?", "Yes. You review and edit every extracted event before creating your planner."],
            ["What if Syllabify gets a date wrong?", "You can review, edit, add, or delete any event before it's saved. Anything unclear is flagged for review."],
            ["Can I delete my syllabus?", "Yes. You can delete any course, event, or your entire account and data at any time."],
            ["Is my syllabus used to train AI?", "No. Your syllabus text is only used to build your schedule. See our Privacy Policy for details."],
          ].map(([q, a], i) => (
            <AccordionItem key={q} value={`item-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left text-slate-900 font-medium">{q}</AccordionTrigger>
              <AccordionContent className="text-slate-600">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-2xl bg-blue-600 px-8 py-14 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white">Ready to organize your semester?</h2>
          <p className="mt-3 text-blue-100">Upload your syllabus and get a plan in under two minutes.</p>
          <Button onClick={onGetStarted} data-testid="final-cta-btn"
            className="mt-7 bg-white text-blue-700 hover:bg-blue-50 px-6 py-6 rounded-lg text-base font-medium">
            <Upload className="h-5 w-5 mr-1" /> Upload My Syllabus
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid sm:grid-cols-2 gap-8">
          <div>
            <Logo />
            <p className="mt-3 text-sm text-slate-500 max-w-xs">Turn your syllabus into a plan.</p>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3 sm:justify-end text-sm">
            <button onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })} className="text-slate-600 hover:text-slate-900">How It Works</button>
            <button onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} className="text-slate-600 hover:text-slate-900">Pricing</button>
            <button onClick={() => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" })} className="text-slate-600 hover:text-slate-900">FAQ</button>
            <button onClick={() => navigate("/privacy")} data-testid="footer-privacy" className="text-slate-600 hover:text-slate-900">Privacy</button>
            <button onClick={() => navigate("/terms")} data-testid="footer-terms" className="text-slate-600 hover:text-slate-900">Terms</button>
            <button onClick={() => navigate("/become-ambassador")} data-testid="footer-ambassador" className="text-slate-600 hover:text-slate-900">Become an Ambassador</button>
            <button onClick={() => navigate("/leaderboard")} data-testid="footer-leaderboard" className="text-slate-600 hover:text-slate-900">Campus Leaderboard</button>
          </div>
        </div>
        <div className="border-t border-slate-100 py-5 text-center text-xs text-slate-400">© 2026 Syllabify</div>
      </footer>
    </div>
  );
}
